import {
  Back,
  CloseCircle,
  CloseSquare,
  Edit,
  InfoCircle,
  Messages,
  Messages2,
  Send,
} from "iconsax-react";
import { useEffect, useState } from "react";
import {
  Link,
  useLoaderData,
  useNavigate,
  useRevalidator,
} from "react-router-dom";
import { toast } from "react-toastify";
import instance from "../../../axios/instance";
import { getToken } from "../../../utils/token";
import { OrderSummaryModal } from "../../../components/modals/order/OrderSummary";
import MessageItem from "../../../components/modals/order/MessageItem";
import { MedicationName } from "../../../components/MedicationName";
import PatientMessageItem from "../../../components/modals/order/PatientMessageItem";
import { NoAccessInfo } from "../../../components/NoAccessInfo";
import Spinner from "../../../components/spinners/Spinner";
import { getTrackingUrl } from "../../../utils/data";
import PaymentCardInfoModal from "../../../components/modals/order/PaymentCardModal";
import CancelOrderConfirmationModal from "../../../components/modals/order/CancelOrderConfirmationModal";
import RequestCancellationConfirmationModal from "../../../components/modals/order/RequestCancellationConfirmationModal";
import dayjs from "dayjs";
import { RotateCcw } from "lucide-react";

const renderMedicationDirections = (currentMedication) => {
  if (
    Array.isArray(currentMedication.directions) &&
    currentMedication.directions.length > 0
  ) {
    return (
      <div className="space-y-1">
        {currentMedication.directions.map((direction, index) => (
          <div
            key={`${direction.month || index}-${direction.label || "direction"}`}
          >
            <span className="font-medium">
              {direction.label || `Month ${index + 1}`}:
            </span>{" "}
            <span>{direction.text || "-/-"}</span>
          </div>
        ))}
      </div>
    );
  }

  return <p className="text-xs">{currentMedication.direction || "-/-"}</p>;
};

export const orderItemLoader = async ({ params }) => {
  const token = getToken();
  const authHeaders = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
  const [order, doctor, plan] = await Promise.all([
    instance.post(
      "/doctor/order/single",
      {
        orderId: params.id,
      },
      authHeaders,
    ),
    instance.get("/doctor", authHeaders),
    instance.get("/doctor/billing/plan/active", authHeaders),
  ]);

  if (!order.data) return { order: null, messages: [], patientMessages: [] };

  const hasNoAccessChat = plan.data.noAccess.includes("in-app-chat");
  const [orderMessages, patientMessages] = hasNoAccessChat
    ? [{ data: [] }, { data: [] }]
    : await Promise.all([
        instance.post(
          "/doctor/messages/facility",
          {
            orderId: order.data.id,
            pharmacyId: order.data.pharmacy_id,
          },
          authHeaders,
        ),
        instance.post(
          "/doctor/messages/patient",
          {
            orderId: order.data.id,
            patientId: order.data.patient_id,
          },
          authHeaders,
        ),
      ]);

  return {
    messages: orderMessages.data,
    order: order.data,
    doctor: doctor.data,
    patientMessages: patientMessages.data,
    plan: plan.data,
  };
};

export const OrderItemPage = () => {
  const { messages, order, doctor, patientMessages, plan, cards } =
    useLoaderData();

  if (!order) {
    toast.error("This order does not exist");
    setTimeout(() => {
      history.back();
    }, 100);
    return;
  }

  const noAccessChat = plan.noAccess.includes("in-app-chat");

  const medications = JSON.parse(order.medication);
  const cancellationRequest = order.cancellation_request;
  const [orderForSummary, setOrder] = useState(null);
  const [openMessage, setOpenMessage] = useState(false);
  const [openCardModal, setOpenCardModal] = useState(false);
  const [openCancelOrderModal, setOpenCancelOrderModal] = useState(false);
  const [openRequestCancellationModal, setOpenRequestCancellationModal] =
    useState(false);
  const [openPatientMessage, setOpenPatientMessage] = useState(false);
  const [loading, setLoading] = useState(null);
  const [unreadMessages, setUnreadMessages] = useState(
    messages.filter(
      (message) =>
        message.status === "unread" &&
        message.recipient_id === order.facility_id &&
        message.order_id === order.id,
    ),
  );
  const [unreadPatientMessages, setUnreadPatientMessages] = useState(
    patientMessages.filter(
      (message) =>
        message.status === "unread" &&
        message.recipient_id === order.facility_id &&
        message.order_id === order.id,
    ),
  );
  const { revalidate } = useRevalidator();
  const navigate = useNavigate();

  const handleGenerateInvoice = async () => {
    if (order.mx_invoice_id) {
      setOpenCardModal(true);
      return;
    }

    if (order.invoice_url) {
      window.open(order.invoice_url, "_blank");
      return;
    }

    try {
      setLoading("invoice");
      const token = getToken();
      const res = await instance.post(
        "/doctor/reps/orders/payment-invoice",
        {
          orderId: order.id,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      setLoading(null);
      if (res.data.url) {
        window.open(res.data.url, "_blank");
      } else {
        setOpenCardModal(true);
      }
      revalidate();
    } catch (error) {
      console.log(error);
      setLoading(null);
      toast.error(
        error.response
          ? error.response.data.message
          : "An error occurred, please try again later",
      );
    }
  };

  const handleCancelOrder = async () => {
    try {
      setLoading("cancel");
      const token = getToken();
      const res = await instance.post(
        "/doctor/reps/orders/cancel",
        {
          orderId: order.id,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      setLoading(null);
      setOpenCancelOrderModal(false);
      revalidate();
    } catch (error) {
      console.log(error);
      setLoading(null);
      toast.error(
        error.response
          ? error.response.data.message
          : "An error occurred, please try again later",
      );
    }
  };

  const handleRequestCancellation = async (reason) => {
    try {
      setLoading("request-cancellation");
      const token = getToken();
      await instance.post(
        "/doctor/reps/orders/cancel-request",
        {
          orderId: order.id,
          reason,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      setLoading(null);
      setOpenRequestCancellationModal(false);
      revalidate();
    } catch (error) {
      console.log(error);
      setLoading(null);
      toast.error(
        error.response
          ? error.response.data.message
          : "An error occurred, please try again later",
      );
    }
  };

  return (
    <>
      {orderForSummary && (
        <OrderSummaryModal
          order={orderForSummary}
          isRefill={false}
          onClose={() => {
            setOrder(null);
          }}
          showNextSteps={
            order.amount && order.payment_status.toLowerCase() !== "paid"
          }
        />
      )}
      {openCardModal && (
        <PaymentCardInfoModal
          onClose={() => setOpenCardModal(null)}
          open={openCardModal}
          address={order.delivery_address}
          zipcode={order.delivery_zipcode}
          orderId={order.id}
          facilityId={order.facility_id}
          triggerRevalidate={() => revalidate()}
        />
      )}
      <CancelOrderConfirmationModal
        isOpen={openCancelOrderModal}
        onClose={() => setOpenCancelOrderModal(false)}
        onConfirm={handleCancelOrder}
        loading={loading === "cancel"}
      />
      <RequestCancellationConfirmationModal
        isOpen={openRequestCancellationModal}
        onClose={() => setOpenRequestCancellationModal(false)}
        onConfirm={handleRequestCancellation}
        loading={loading === "request-cancellation"}
      />
      <div className="fixed top-0 right-0 h-[100vh] w-[450px] bg-white px-3 py-10 overflow-y-scroll z-30">
        <div className="flex items-center justify-between">
          <div className="text-[18px] font-medium">
            Order Information - #{order.id}
          </div>
          <button onClick={() => navigate("..")}>
            <CloseSquare variant="Bold" />
          </button>
        </div>
        {order.order_status === "canceled" && (
          <div className="px-3 py-1 w-fit flex gap-x-1 rounded-md bg-red-50 text-red-800 text-xs mt-1">
            Canceled <CloseCircle size={16} variant="Outline" />
          </div>
        )}
        {order.order_status === "rejected" && (
          <div className="px-3 py-1 w-fit flex gap-x-1 rounded-md bg-red-50 text-red-800 text-xs mt-1">
            Rejected <RotateCcw size={16} variant="Outline" />
          </div>
        )}
        {order.cancellation_request != null && (
          <div className="px-3 py-1 w-fit flex gap-x-1 rounded-md bg-amber-50 text-amber-800 text-xs mt-1">
            Cancellation Request <InfoCircle size={16} variant="Outline" />
          </div>
        )}
        <div className="border rounded-md px-3 py-3 mt-4 grid grid-cols-2 items-start gap-x-5 gap-y-5">
          <div>
            <h2 className="text-sm mb-1 capitalize text-[black] font-[500]">
              Sup. Practitioner:
            </h2>
            <p className="text-xs capitalize">
              {order.doctor.lastname + " " + order.doctor.firstname}
            </p>
          </div>
          <div>
            <h2 className="text-sm mb-1 capitalize text-[black] font-[500]">
              Pharmacy:
            </h2>
            <p className="text-xs capitalize">{order.pharmacy.pharmacy_name}</p>
          </div>
          {order.order_method.toLowerCase() === "patient" && (
            <div>
              <h2 className="text-sm mb-1 capitalize text-[black] font-[500]">
                Patient:
              </h2>
              {["pharmacy", "e-prescription"].includes(order.order_source) ? (
                <p className="text-xs capitalize">
                  {order.patient.lastname + " " + order.patient.firstname}
                </p>
              ) : (
                <Link
                  to={`/dashboard/patients/${order.patient.id}`}
                  className="inline-block underline text-primary text-xs capitalize"
                >
                  {order.patient.lastname + " " + order.patient.firstname}
                </Link>
              )}
            </div>
          )}
          <div>
            <h2 className="text-sm mb-1 capitalize text-[black] font-[500]">
              Order Type:
            </h2>
            <p className="text-xs">{order.order_type}</p>
          </div>
          <div>
            <h2 className="text-sm mb-1 capitalize text-[black] font-[500]">
              Order Status:
            </h2>
            <p className="text-xs capitalize">{order.order_status}</p>
          </div>
          <div>
            <h2 className="text-sm mb-1 capitalize text-[black] font-[500]">
              Order Step:
            </h2>
            <p className="text-xs capitalize">{order.step}</p>
          </div>
          <div>
            <h2 className="text-sm mb-1 capitalize text-[black] font-[500]">
              Order Method:
            </h2>
            <p className="text-xs">{order.order_method}</p>
          </div>
          <div>
            <h2 className="text-sm mb-1 capitalize text-[black] font-[500]">
              Billing Preference:
            </h2>
            <p className="text-xs">{order.order_billing_preference}</p>
          </div>
          <div>
            <h2 className="text-sm mb-1 capitalize text-[black] font-[500]">
              Shipping Service:
            </h2>
            <p className="text-xs capitalize">
              {order.shipping_service
                ? JSON.parse(order.shipping_service).name
                : "--"}
            </p>
          </div>
          <div>
            <h2 className="text-sm mb-1 capitalize text-[black] font-[500]">
              Delivery Phone:
            </h2>
            <p className="text-xs">{order.delivery_phone}</p>
          </div>
          <div>
            <h2 className="text-sm mb-1 capitalize text-[black] font-[500]">
              Delivery Address:
            </h2>
            <p className="text-xs">
              {order.delivery_address}, {order.delivery_city}
              {order.delivery_state ? `. ${order.delivery_state}` : ""}{" "}
              {order.delivery_zipcode ? `(${order.delivery_zipcode})` : ""}
            </p>
          </div>
          <div>
            <h2 className="text-sm mb-1 capitalize text-[black] font-[500]">
              Delivery Type:
            </h2>
            <p className="text-xs">{order.delivery_type}</p>
          </div>
          {order.amount && (
            <div>
              <h2 className="text-sm mb-1 capitalize text-[black] font-[500]">
                Amount:
              </h2>
              <p className="text-xs">${order.amount.toLocaleString()}</p>
            </div>
          )}

          {["processed", "shipped"].includes(order.order_status) && (
            <div>
              <h2 className="text-sm mb-1 capitalize text-[black] font-[500]">
                RX(s):
              </h2>
              <p
                className="text-xs text-primary underline cursor-pointer"
                onClick={() => navigate(`/dashboard/orders/${order.id}/rxs`)}
              >
                View Dispensed Rx
              </p>
            </div>
          )}
          <div>
            <h2 className="text-sm mb-1 capitalize text-[black] font-[500]">
              Summary:
            </h2>
            <p
              className="text-xs text-primary underline cursor-pointer"
              onClick={() => setOrder(order)}
            >
              View summary
            </p>
          </div>
        </div>
        {order.amount && order.payment_status.toLowerCase() !== "paid" && (
          <button
            className="text-base my-5 w-full h-[40px] flex items-center justify-center bg-primary text-white rounded-md py-2 px-2 hover:opacity-80 duration-200 disabled:opacity-70 disabled:cursor-not-allowed"
            onClick={handleGenerateInvoice}
            disabled={
              order.payment_status.toLowerCase() === "refunded" ||
              loading === "invoice"
            }
          >
            {loading === "invoice" ? (
              <Spinner color={"white"} />
            ) : (
              "Pay for order"
            )}
          </button>
        )}
        {!noAccessChat &&
          order.order_method.toLowerCase() === "patient" &&
          order.order_source.toLowerCase() !== "pharmacy" && (
            <div className="my-3">
              <button
                className="flex items-center gap-x-1 w-fit max-w-fit justify-center px-4 py-2 rounded-md border border-primary text-sm relative"
                onClick={() => setOpenPatientMessage(true)}
              >
                <Messages size={20} />
                Message Patient
                {unreadPatientMessages.length >= 1 && (
                  <span className="block absolute top-2 right-2 w-[5px] h-[5px] bg-[tomato] rounded-full"></span>
                )}
              </button>
            </div>
          )}
        {medications.length >= 1 ? (
          <MedicationSummary
            medications={medications}
            medType={order.med_type}
            orderMethod={order.order_method}
          />
        ) : (
          <span className="block mt-2 text-sm opacity-80">
            No medication specified
          </span>
        )}
        {order.pharmacy_note && (
          <>
            <h3 className="bg-[#eee] my-3 py-2 text-center font-medium rounded-md text-sm">
              Note from Pharmacy
            </h3>
            <div className="border rounded-md px-3 py-3 mt-5">
              <span className="block mt-2 text-sm opacity-80">
                {order.pharmacy_note}
              </span>
            </div>
          </>
        )}
        <h3 className="bg-[#eee] my-3 py-2 text-center font-medium rounded-md text-sm">
          Track Order
        </h3>
        <OrderTracker
          status={order.order_status}
          deliveryService={String(order.delivery_service || "").toLowerCase()}
        />
        {order.order_status.toLowerCase() === "placed" && (
          <button
            className="text-base my-5 w-full h-[40px] flex items-center justify-center bg-red-500 text-white rounded-md py-2 px-2 hover:opacity-80 duration-200 disabled:opacity-70 disabled:cursor-not-allowed"
            onClick={() => setOpenCancelOrderModal(true)}
            disabled={loading === "cancel"}
          >
            {loading === "cancel" ? (
              <Spinner color={"white"} />
            ) : (
              "Cancel Order"
            )}
          </button>
        )}
        {cancellationRequest ? (
          <div>
            <h3 className="bg-[#eee] my-3 py-2 text-center font-medium rounded-md text-sm">
              Cancellation Request
            </h3>
            <div className="border rounded-md px-3 py-3 mt-5 grid grid-cols-1 gap-y-4">
              <div>
                <h2 className="text-sm mb-1 capitalize text-[black] font-[500]">
                  Reason:
                </h2>
                <p className="text-xs">{cancellationRequest.reason || "-/-"}</p>
              </div>
              <div>
                <h2 className="text-sm mb-1 capitalize text-[black] font-[500]">
                  Requested At:
                </h2>
                <p className="text-xs">
                  {dayjs(cancellationRequest.requested_at).format(
                    "DD MMM, YYYY [at] hh:mm",
                  )}
                </p>
              </div>
            </div>
          </div>
        ) : (
          !["placed", "shipped", "rejected", "canceled"].includes(
            order.order_status.toLowerCase(),
          ) && (
            <button
              className="text-base my-5 w-full h-[40px] flex items-center justify-center bg-red-500 text-white rounded-md py-2 px-2 hover:opacity-80 duration-200 disabled:opacity-70 disabled:cursor-not-allowed"
              onClick={() => setOpenRequestCancellationModal(true)}
              disabled={loading === "request-cancellation"}
            >
              {loading === "request-cancellation" ? (
                <Spinner color={"white"} />
              ) : (
                "Request Cancellation"
              )}
            </button>
          )
        )}
        {order.order_status === "rejected" && (
          <>
            <h3 className="bg-[#eee] my-3 py-2 text-center font-medium rounded-md text-sm">
              Reason for rejection
            </h3>
            <span className="block mt-2 text-sm opacity-80">
              {order.rejection_note}
            </span>
          </>
        )}
        {order.order_status === "shipped" &&
          String(order.delivery_service || "").toLowerCase() !== "pickup" &&
          order.shipping && (
            <>
              <h3 className="bg-[#eee] my-3 py-2 text-center font-medium rounded-md text-sm">
                Track Shipping
              </h3>
              <span className="block mt-2 text-sm opacity-80">
                Shipping Track ID ({order.shipping.delivery_service}):{" "}
                <a
                  href={getTrackingUrl(
                    order.shipping.delivery_service,
                    order.tracking_id,
                  )}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline text-primary hover:opacity-80 duration-300"
                >
                  {order.tracking_id}
                </a>
              </span>
            </>
          )}
        {!noAccessChat && (
          <div
            className="fixed bottom-10 right-10 bg-primary h-[45px] w-[45px] rounded-full flex items-center justify-center cursor-pointer"
            onClick={() => setOpenMessage(true)}
          >
            <Messages2 size="28" color="white" />
            {unreadMessages.length > 0 && (
              <span className="flex items-center justify-center w-[17px] h-[17px] rounded-full bg-[tomato] absolute top-0 -right-[5px] text-[10px] text-white">
                {unreadMessages.length > 9 ? "9+" : unreadMessages.length}
              </span>
            )}
          </div>
        )}
        {noAccessChat && (
          <NoAccessInfo
            msg={`Upgrade your plan from ${plan.name} to chat with pharmacies and patients`}
          />
        )}
        {openMessage && (
          <MessageItem
            order={order}
            onClose={() => setOpenMessage(false)}
            dbMessages={messages}
            doctor={doctor}
            isOpen={openMessage}
            setUnreadMessages={setUnreadMessages}
          />
        )}
        {order.order_method.toLowerCase() === "patient" &&
          openPatientMessage && (
            <PatientMessageItem
              order={order}
              onClose={() => setOpenPatientMessage(false)}
              dbMessages={patientMessages}
              doctor={doctor}
              setUnreadMessages={setUnreadPatientMessages}
            />
          )}
      </div>
      <div
        className="fixed z-20 inset-0 bg-[rgba(0,0,0,.2)]"
        onClick={() => {
          history.back();
          setOpenMessage(false);
          setOpenPatientMessage(false);
        }}
      />
    </>
  );
};

const OrderTracker = ({ status, deliveryService }) => {
  const [statuses, setStatus] = useState([
    {
      value: "placed",
      desc: "Order has been placed",
    },
    {
      value: "accepted",
      desc: "Order has been accepted",
    },
    {
      value: "processing",
      desc: "Order is being processed",
    },
    {
      value: "processed",
      desc: "Order is pending shipping",
    },
    {
      value: "shipped",
      desc:
        deliveryService === "pickup"
          ? "Order has been picked up"
          : "Order has been shipped",
    },
  ]);
  const [foundIndex, setFoundIndex] = useState(0);
  useEffect(() => {
    if (status.toLowerCase() === "rejected") {
      const stat = [
        statuses[0],
        { value: "rejected", desc: "Order was rejected" },
      ];
      setStatus(stat);
      setFoundIndex(1);
    } else {
      statuses.forEach((stat, index) => {
        if (stat.value === status.toLowerCase()) {
          setFoundIndex(index);
        }
      });
    }
  }, []);

  const filteredStatuses = statuses
    .slice(0, foundIndex + 1)
    .map((val) => val.value);
  return (
    <div className="mt-5">
      {statuses.map((stat, index) => {
        const active = filteredStatuses.includes(stat.value);
        return (
          <div
            className="mb-10 grid grid-cols-[30px_auto] gap-x-3 items-center"
            key={index}
          >
            <div className="relative">
              {active ? (
                stat.value !== "rejected" ? (
                  <span className="h-[30px] w-[30px] rounded-full flex items-center justify-center bg-primary text-white">
                    &#x2714;
                  </span>
                ) : (
                  <span className="h-[30px] w-[30px] rounded-full flex items-center justify-center bg-[tomato] border-primary border-2 text-white">
                    x
                  </span>
                )
              ) : (
                <span className="h-[30px] w-[30px] rounded-full flex items-center justify-center bg-white border-8 border-primary text-white"></span>
              )}

              <span
                className={`block w-[2px] ${
                  index === statuses.length - 1
                    ? "h-[0]"
                    : "h-[calc(100%_+_2.5rem)]"
                } absolute top-[30px] left-1/2 -translate-x-1/2 bg-primary`}
              ></span>
            </div>
            <div className="text-sm">{stat.desc}</div>
          </div>
        );
      })}
    </div>
  );
};

const MedicationSummary = ({ medications, medType, orderMethod }) => {
  const medication = medications[0];
  return (
    <>
      {medType === "compound" ? (
        <div>
          <h3 className="bg-[#eee] my-3 py-2 text-center font-medium rounded-md text-sm">
            Compound Medication
          </h3>
          <div className="border rounded-md px-3 py-3 grid grid-cols-2 items-start gap-x-5 gap-y-5">
            <div className="text-sm mb-1">
              <h2 className="capitalize text-[black] font-[500]">Name:</h2>
              <div className="text-xs">{medication.name}</div>
            </div>
            <div className="text-sm mb-1">
              <h2 className="capitalize text-[black] font-[500]">Category:</h2>
              <p className="text-xs">{medication.category}</p>
            </div>
            <div className="text-sm mb-1">
              <h2 className="capitalize text-[black] font-[500]">Size:</h2>
              <p className="text-xs">{medication.size}</p>
            </div>
            <div className="text-sm mb-1">
              <h2 className="capitalize text-[black] font-[500]">SIG:</h2>
              <p className="text-xs">{medication.sig}</p>
            </div>
            <div className="text-sm mb-1">
              <h2 className="capitalize text-[black] font-[500]">Quantity:</h2>
              <p className="text-xs">{medication.quantity}</p>
            </div>

            <div className="text-sm mb-1">
              <h2 className="capitalize text-[black] font-[500]">
                Total Ingredients:
              </h2>
              <p className="text-xs">{medication.ingredients.length}</p>
            </div>
            <div className="col-span-2">
              {medication.reasonForCompounding && (
                <div className="text-sm mb-1">
                  <h2 className="capitalize text-[black] font-[500]">
                    Medication Necessity Justification:
                  </h2>
                  <p className="text-xs">{medication.reasonForCompounding}</p>
                </div>
              )}
              {medication.additionalNote && (
                <div className="text-sm mb-1">
                  <h2 className="capitalize text-[black] font-[500]">
                    Additional Note:
                  </h2>
                  <p className="text-xs">{medication.additionalNote}</p>
                </div>
              )}
            </div>
          </div>
          <h3 className="bg-[#eee] my-3 py-2 text-center font-medium rounded-md text-sm">
            Ingredients
          </h3>
          <div className="border rounded-md px-3 py-3 grid grid-cols-1 items-start gap-x-5 gap-y-5">
            {medication.ingredients.map((ingredient, index) => (
              <div key={index} className="flex gap-x-2 items-center">
                <h2 className="text-sm capitalize text-[black] font-[500]">
                  {ingredient.name} -{" "}
                </h2>
                <p className="text-sm">{ingredient.quantity}</p>
              </div>
            ))}
          </div>
        </div>
      ) : (
        medications.map((medication, index) => (
          <div key={index}>
            <h3 className="bg-[#eee] my-3 py-2 text-center font-medium rounded-md text-sm">
              Medication {index + 1}
            </h3>
            <div className="border rounded-md px-3 py-3 grid grid-cols-2 items-start gap-x-5 gap-y-5">
              <div className="text-sm mb-1">
                <h2 className="capitalize text-[black] font-[500]">Name:</h2>
                <div className="text-xs">
                  <MedicationName med={medication} />
                </div>
              </div>

              {orderMethod === "Patient" ? (
                <div className="text-sm mb-1">
                  <h2 className="capitalize text-[black] font-[500]">
                    Days of Supply:
                  </h2>
                  <p className="text-xs">{medication.daysOfSupply}</p>
                </div>
              ) : (
                <div className="text-sm mb-1">
                  <h2 className="capitalize text-[black] font-[500]">
                    Quantity:
                  </h2>
                  <p className="text-xs">{medication.quantity}</p>
                </div>
              )}

              <div className="text-sm mb-1">
                <h2 className="capitalize text-[black] font-[500]">mg/mL:</h2>
                <p className="text-xs">{medication.mg}</p>
              </div>
              <div className="text-sm mb-1">
                <h2 className="capitalize text-[black] font-[500]">Size:</h2>
                <p className="text-xs">{medication.size}</p>
              </div>
              <div className="text-sm mb-1">
                <h2 className="capitalize text-[black] font-[500]">Device:</h2>
                <p className="text-xs">{medication.device}</p>
              </div>
              <div className="text-sm mb-1">
                <h2 className="capitalize text-[black] font-[500]">
                  Route of Admin:
                </h2>
                <p className="text-xs">{medication.route_of_admin}</p>
              </div>
              <div className="col-span-2 text-sm mb-1">
                <h2 className="capitalize text-[black] font-[500]">
                  Directions:
                </h2>
                <div className="text-xs">
                  {renderMedicationDirections(medication)}
                </div>
              </div>
              <div className="col-span-2">
                {medication.reasonForCompounding && (
                  <div className="text-sm mb-1">
                    <h2 className="capitalize text-[black] font-[500]">
                      Medication Necessity Justification:
                    </h2>
                    <p className="text-xs">{medication.reasonForCompounding}</p>
                  </div>
                )}
                {medication.additionalNote && (
                  <div className="text-sm mb-1">
                    <h2 className="capitalize text-[black] font-[500]">
                      Additional Note:
                    </h2>
                    <p className="text-xs">{medication.additionalNote}</p>
                  </div>
                )}
              </div>
              {medication.form && (
                <div className="text-sm mb-1">
                  <h2 className="capitalize text-[black] font-[500]">Form:</h2>
                  <p className="text-xs">{medication.form}</p>
                </div>
              )}
              {medication.strength && (
                <div className="text-sm mb-1">
                  <h2 className="capitalize text-[black] font-[500]">
                    Strength:
                  </h2>
                  <p className="text-xs">{medication.strength}</p>
                </div>
              )}
              {medication.category && (
                <div className="text-sm mb-1">
                  <h2 className="capitalize text-[black] font-[500]">
                    Category:
                  </h2>
                  <p className="text-xs">{medication.category}</p>
                </div>
              )}
            </div>
          </div>
        ))
      )}
    </>
  );
};
